<template>
  <div class="line-analysis" v-if="checked.length > 0" :updated="forceUpdate">
    <!--
      Tab Header
    -->
    <div class="tab-header">
      <a href="#!" v-for="tab in tabs" :class="{'active': tabActive === tab.id}" @click="tabActive = tab.id">{{ tab.name|trans }}</a>
    </div>

    <!--
      Tab Content
    -->
    <div class="tab-content">
      <div v-for="tab in tabs" v-if="tabActive === tab.id">
        <svg :id="tab.id" :ref="tab.id"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import hslLighten from "../Converter/hslLighten";
import EventBus from './eventBus';

export default {
  props: ['selected', 'checked', 'options'],
  data() {
    return {
      tabs: [
        {id: 'azimuth', name: 'Azimuth'},
        {id: 'elevation', name: 'Elevation'},
        {id: 'pressure', name: 'Pressure'},
        {id: 'is_hover', name: 'Hover'},
        {id: 'x', name: 'X Coords'},
        {id: 'y', name: 'Y Coords'},
      ],
      tabActive: 'azimuth',
      forceUpdate: false,

      svgMargin: {
        top: 5,
        left: 30,
        right: 30,
        bottom: 30,
      },
      svgWidth: 700,
      svgHeight: 260,

      vLine: null
    }
  },

  watch: {
    'options.hover': function (newData, oldData) {
      this.forceUpdate = !this.forceUpdate;
    },
    'options.viewSignature': function (newData, oldData) {
      this.forceUpdate = !this.forceUpdate;
    },
  },

  updated() {
    // Off Events
    this.getCheckedData.forEach((signature) => {
      EventBus.$off('onPointHover' + signature.id);
    })

    let svg = this.$refs[this.tabActive][0];
    if (svg) {
      this.svgWidth = svg.clientWidth + this.svgMargin.left;
      this.draw(d3.select(svg));
    }
  },

  computed: {
    getAllBiometricData() {
      let biometricData = [];

      this.getCheckedData.forEach((signature) => {
        // Reset Time
        let t = signature.biometricData[0].time;
        signature.biometricData.map((i) => (i.time -= t));

        biometricData.push(...signature.biometricData)
      })

      return biometricData;
    },

    getCheckedData() {
      let signatures = JSON.parse(JSON.stringify(this.checked));

      if (!this.options.hover || !this.options.viewSignature) {
        return signatures.map((signature) => {
          return {
            ...signature,
            biometricData: signature.biometricData.filter((data) => {
              if (!this.options.hover && !this.options.viewSignature) {
                return false;
              }

              if (!this.options.hover) {
                return !data.is_hover;
              }

              if (!this.options.viewSignature) {
                return data.is_hover;
              }

              return true;
            })
          }
        })
      }

      return signatures;
    }
  },

  methods: {
    draw(svg) {
      // Clear SVG
      svg.selectAll("*").remove();

      // Width|Height
      svg.attr("viewBox", `0 0 ${this.svgWidth} ${this.svgHeight + this.svgMargin.bottom}`);

      // Axis Generator
      let cAxis = svg.append("g");
      let x = d3.scaleLinear()
        .domain(d3.extent(this.getAllBiometricData, d => d.time))
        .range([0, this.svgWidth - this.svgMargin.right]);
      let y = d3.scaleLinear()
        .domain(d3.extent(this.getAllBiometricData, d => d[this.tabActive]))
        .range([this.svgHeight, 0]);

      this.xAxisGenerate(x, cAxis);
      this.yAxisGenerate(y, cAxis);

      // Draw Line Data
      let paths = svg.append('g').attr("transform", `translate(${this.svgMargin.left},${this.svgMargin.top})`);
      let line = d3.line().x((d) => x(d.time)).y((d) => y(d[this.tabActive]));
      this.getCheckedData.forEach((signature, index) => {
        paths.append("path")
          .datum(signature.biometricData)
          .attr("stroke-width", "2px")
          .attr("stroke", hslLighten(signature.color, 40))
          .attr("fill", "none")
          .attr("d", line);

        // Draw Vertical Line
        this.drawVerticalLine(index === 0, paths, signature, x);
      })
    },

    /**
     * Signature onPointHover Event
     */
    drawVerticalLine(verticalLine, paths, signature, x) {
      if (verticalLine) {
        this.vLine = paths.append('line')
          .attr("x1", 0).attr("y1", 0)
          .attr("x2", 0).attr("y2", this.svgHeight)
          .attr("stroke", "red")
          .attr('class', 'verticalLine');
      }

      EventBus.$on('onPointHover' + signature.id, ({el, point}) => {
        let samePoint;
        if (!signature) {
          return;
        }

        if (this.options.hover) {
          signature = this.checked.find((d) => d.id === signature.id);
          samePoint = Object.assign({}, signature.biometricData.find((d) => {
            return (d.time === point.time)
          }));

          samePoint.time -= signature.biometricData[0].time;
        } else {
          samePoint = signature.biometricData.find((d) => {
            return (d.azimuth === point.azimuth) && (d.pressure === point.pressure) && (d.hover === point.hover) && (d.elevation === point.elevation)
          });
        }

        if (samePoint) {
          this.vLine.attr("x1", x(samePoint.time));
          this.vLine.attr("x2", x(samePoint.time));
        }
      });
    },

    /**
     * Chart X Axis Generator
     */
    xAxisGenerate(x, axisGroup) {
      axisGroup
        .append("g")
        .attr("class", "axis x-axis")
        .attr("transform", `translate(${this.svgMargin.left},${this.svgHeight + this.svgMargin.top})`)
        .call(d3.axisBottom(x));
    },

    /**
     * Chart Y Axis Generator
     */
    yAxisGenerate(y, axisGroup) {
      axisGroup
        .append("g")
        .attr("class", "axis y-axis")
        .attr("transform", `translate(${this.svgMargin.left},${this.svgMargin.top})`)
        .call(d3.axisLeft(y))
        .append("text")
        .attr("fill", "#000")
        .style("text-anchor", "end");
    },
  }
}
</script>

<style lang="scss">
.line-analysis {
  .tab-header {
    background: #f1f1f1;
    border-top: 2px solid #e9e9e9;
    border-bottom: 2px solid #e9e9e9;

    a {
      display: inline-flex;
      padding: 8px 15px;
      margin-top: -2px;
      margin-bottom: -2px;
      transition: .2s all;
      color: rgb(83, 93, 101);

      &:hover {
        background: rgb(103, 116, 122);
        color: #FFF;
      }

      &.active {
        background: rgb(83, 94, 99);
        color: #FFF;
      }
    }
  }

  .tab-content {
    max-height: 300px;
    padding: 10px;
    display: inline-flex;
    width: 100%;

    & > div {
      width: 100%;
      display: inline-flex;
    }

    svg {
      width: 100%;
    }
  }
}
</style>
